import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { nonDraftRfqCount } from '../../verticals/rfq/domain'
import { apiClient } from '../api/client'
import { useAuth } from '../auth/auth-context'
import { useRfqCount } from './rfq-count-context'

/**
 * UID-529 (ADR-0167, amended) — hydrates the Sidebar "RFQ" nav badge with
 * the count of SUBMITTED RFQ records — every status `GET /rfq` returns
 * EXCEPT `draft` (`data.filter((rfq) => rfq.status !== 'draft').length`).
 *
 * Amendment rationale: the badge must show exactly the number of RFQs a
 * customer can see on the `/rfq` list page, never a raw backend/DB total.
 * `draft`-status rows are created eagerly (ADR-0082) the moment compose
 * opens, and the list page's own Draft tab does NOT render one card per real
 * `draft` row — it renders one card per CLIENT SLOT the current session is
 * holding (`RfqListPage.tsx`, ADR-0172 D2). Counting every real `draft` row
 * here would silently disagree with what's on screen: an orphaned draft from
 * an abandoned session is invisible on the list page but would still inflate
 * the badge.
 *
 * UID-551 has SHIPPED (as part of UID-552), and this exclusion is now the
 * DURABLE RULE rather than a stopgap — ADR-0172 D4 says so in as many words
 * ("`useHydrateRfqCount` is UNCHANGED … it must now **not** change"). The
 * earlier version of this comment predicted UID-551 would reconcile the two
 * surfaces by making the list page render every real server `draft` row;
 * ADR-0172 D2 deliberately chose the NARROWER answer (client slots only) and
 * D6 records why: ADR-0108 D1's TTL reaper is still unbuilt, so the server's
 * `draft` population is the accumulated residue of every abandoned compose,
 * and surfacing it would fill the tab with junk and make this badge climb
 * monotonically. The union of server rows and client slots is the correct end
 * state and is DEFERRED ON that reaper (ADR-0172 D6, alternative 1) — not
 * rejected, and not something to reintroduce here on its own, which would be
 * strictly worse than either (the badge would count rows the tab does not
 * render).
 *
 * `Sidebar.tsx` adds the current session's own draft count back on top of
 * this number — `countOpenDrafts(rfqBundle.slots)`, the same shared seam the
 * list page's Draft-tab badge and card list read (ADR-0172 D4) — so the
 * final displayed badge matches the sum of everything actually rendered
 * across all four tabs.
 *
 * Meant to be called once, from `<AppShell>` (mounted inside both
 * `<AuthProvider>` and `<RfqCountProvider>` — see `App.tsx`), same seam as
 * `useHydrateCartCount`.
 *
 * DIFFERENCE FROM `useHydrateCartCount`: that hook fires at most once per
 * session (there is a complete set of push-on-mutation call sites for the
 * cart). RFQs have no such complete set — five write paths across three
 * files (submit, resubmit, abandon, share-as-link create, accept) — so
 * instead this refetches on every `location.pathname` change, the same
 * precedent `Sidebar.tsx`'s `useUploadSessions` refetch already uses for
 * exactly this reason (see that hook's call site comment). This is NOT
 * timer polling — nothing in this app polls — it only refires on navigation,
 * the natural point a user is likely to glance at the sidebar again. Submit
 * itself needs no separate push: `RfqReviewPage`'s submit flow already
 * `navigate('/rfq')`s, which IS a route change, so this refetch covers it
 * for free.
 *
 * Gated on `auth.status === 'authenticated'` — never fires while `loading`,
 * `unauthenticated`, or `onboarding_required`. On failure (network error,
 * non-2xx, missing data) the count is left UNTOUCHED rather than zeroed —
 * same failure posture as `useHydrateCartCount` — so a transient failure
 * never makes an active account's badge lie about having zero RFQs; the
 * badge self-heals on the next route change.
 *
 * DEDUPE (perf follow-up, no functional change): `/rfq` (`RfqListPage`)
 * already fetches `GET /rfq` itself (per-tab, UID-603), and pushes the same
 * `statusCounts`-derived total into this same `setTotalCount` right after
 * (see that page's `useRfqList`/`useRfqCount` wiring). Firing THIS hook's
 * independent fetch on that exact route transition would just be a second,
 * wasted network round-trip for a number `RfqListPage` is about to supply
 * anyway — so this hook skips its own fetch when `location.pathname ===
 * '/rfq'` and lets the list page's own load be the sole source there. Every
 * OTHER route (where `RfqListPage` isn't mounted) is unaffected — this hook
 * still fetches and hydrates the badge normally there.
 *
 * UID-603 (ADR-0176 D5/D6 row 1) — the count no longer comes from the rows
 * at all: it reads the envelope's page-independent `statusCounts` (summed
 * over every non-`draft` status via the SAME `nonDraftRfqCount` helper
 * `RfqListPage` uses, so the two can never independently drift), rather than
 * `data.filter((rfq) => rfq.status !== 'draft').length` off a (now paged)
 * `items` array. It sends `?limit=1` and no `cursor`: `statusCounts` is
 * emitted whenever `cursor` is absent (D5), so the smallest page the server
 * will give is the cheapest possible probe for it — the rows themselves are
 * incidental, and this hook no longer reads them at all.
 */
export function useHydrateRfqCount() {
  const auth = useAuth()
  const location = useLocation()
  const { setTotalCount } = useRfqCount()

  // Refetch on route change (see doc comment) and on an auth-status
  // transition into `authenticated`. `location.pathname` is now also READ in
  // the body (the DEDUPE early-return above) as well as being a re-run
  // trigger, so the exhaustive-deps rule is satisfied without a suppression.
  // `setTotalCount` is stable (useMemo'd in the provider) and not itself a
  // re-trigger source.
  useEffect(() => {
    if (auth.status !== 'authenticated') return
    // See DEDUPE doc comment above — `RfqListPage` supplies the count itself
    // on this exact route.
    if (location.pathname === '/rfq') return

    let cancelled = false
    void (async () => {
      try {
        // A 401 is handled globally (authMiddleware flips auth.status); any
        // other error/missing-data case intentionally falls through to
        // "leave the badge untouched" — see this function's doc comment —
        // so `error` itself is never read here.
        // UID-603 (ADR-0176 D5/D6 #1) — `limit=1` PROBE, not a full read.
        //
        // The badge used to be `data.filter(status !== 'draft').length` over the
        // WHOLE account's RFQs, which meant this hook downloaded every RFQ (with
        // its `lines` array and its whole enrichment tail) on every route change
        // to compute one integer. `statusCounts` is now authoritative and is
        // ACCOUNT-FRAMED, UNFILTERED and PAGE-INDEPENDENT, so the count no longer
        // comes from the rows at all — one row is fetched because the response
        // has to carry SOMETHING, not because the badge needs it.
        const { data } = await apiClient.GET('/rfq', { params: { query: { limit: 1 } } })
        if (cancelled) return
        // `statusCounts` is present on the first page of a scroll (no `cursor`),
        // which this always is. Guarded anyway rather than asserted: the existing
        // posture of this hook is "never zero the badge on a failure", and a
        // missing field is a failure like any other.
        //
        // The sum itself goes through the SHARED `nonDraftRfqCount` — the same
        // fold `RfqListPage` applies to the same field. That is ADR-0167
        // Amendment 1's "compute the count ONCE and share it" honoured
        // literally: one derivation in one place, so the sidebar badge and the
        // list page cannot drift. Filter-independent by construction, too —
        // `statusCounts` never sees the list's status/tab filter, so opening a
        // filtered list cannot move the badge.
        if (data?.statusCounts) setTotalCount(nonDraftRfqCount(data.statusCounts))
      } catch {
        // Network/parse error — leave the badge untouched (see doc comment).
      }
    })()

    return () => {
      cancelled = true
    }
  }, [auth.status, location.pathname, setTotalCount])
}
